import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { PortfolioItem } from 'Douleutaras/types/portfolio';
import { ReduxEntity } from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { normalizeItems } from 'Douleutaras/utilities/redux/normalizeItems';
import { LANGUAGE_CODES } from 'Douleutaras/constants/system';
import { API_SUCCESS_STATUS, HTTP_SUCCESS_CODES } from 'Douleutaras/constants/api';

const PORTFOLIOS_ENDPOINT = 'api/public/portfolio';
const PORTFOLIOS_BULK_ORDER_UPDATE_ENDPOINT = 'api/public/portfolio/bulk-update-order';

const portfolioAdapter = createEntityAdapter<PortfolioItem>({
  selectId: item => item.id
});

export const getPortfolios = createAsyncThunk(
  'user/portfolios/get',
  async (args: { user: string; [key: string]: string; }, api) => {
    const response = await makeRequest(
      `${PORTFOLIOS_ENDPOINT}/`,
      {
        method: 'GET',
        additionalGetParams: {
          ...args
        }
      })(api);

    return normalizeItems(response.data);
  }
);

interface PortfolioOrder {
  id: string;
  order: number;
}

const isResponseValid = (status: number, apiStatus: string | undefined): boolean =>
  HTTP_SUCCESS_CODES.includes(status) && apiStatus === API_SUCCESS_STATUS;

export const updateBulkPortfolioOrder = createAsyncThunk(
  'user/portfolios/update-order-bulk',
  async (portfolioOrder: Array<PortfolioOrder>, thunkApi) => {
    const response = await makeRequest(PORTFOLIOS_BULK_ORDER_UPDATE_ENDPOINT, {
      method: 'POST',
      body: portfolioOrder
    })(thunkApi);

    const { status, statusText } = response;

    if (!isResponseValid(status, statusText)) {
      return thunkApi.rejectWithValue({ error: 'Error updating portfolio order' });
    }

    return portfolioOrder;
  }
);

export const updatePortfolio = createAsyncThunk(
  'user/portfolio/update',
  async ({ id, values, lang }: { id: string, values: { [k: string]: string | number }, lang?: LANGUAGE_CODES }, api) => {

    const additionalRequestOptions = {
      additionalHeaders: undefined
    };

    // If language is defined, send a languagecode header which saves values to different language
    if (lang) {
      additionalRequestOptions.additionalHeaders = {
        languagecode: lang
      };
    }

    const response = await makeRequest(
      `${PORTFOLIOS_ENDPOINT}/${id}`,
      {
        method: 'PUT',
        body: {
          ...values
        },
        ...additionalRequestOptions
      }
    )(api);

    return normalizeItems([ response.data ]);
  }
);

export const addPortfolio = createAsyncThunk(
  'user/portfolio/add',
  async ({ values }: { values: { [k: string]: string } }, api) => {

    const response = await makeRequest(
      `${PORTFOLIOS_ENDPOINT}/`,
      {
        method: 'POST',
        body: {
          ...values
        }
      }
    )(api);

    return { data: normalizeItems([ response.data ]), createdId: response.data.id };
  }
);

export const removePortfolio = createAsyncThunk(
  'user/portfolio/delete',
  async (id: number, api) => {
    const response = await makeRequest(
      `${PORTFOLIOS_ENDPOINT}/${id}/`,
      {
        method: 'DELETE'
      }
    )(api);

    return id;
  }
);

export const portfolioSlice = createSlice({
  name: 'portfolios',
  initialState: portfolioAdapter.getInitialState({
    loading: false,
    error: false,
    entities: {}
  } as ReduxEntity<PortfolioItem>),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getPortfolios.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getPortfolios.fulfilled, (state, action) => {
      portfolioAdapter.upsertMany(state, action.payload);
      state.loading = false;
    });
    builder.addCase(addPortfolio.fulfilled, (state, action) => {
      portfolioAdapter.upsertMany(state, action.payload.data);
      state.loading = false;
    });
    builder.addCase(updatePortfolio.fulfilled, (state, action) => {
      portfolioAdapter.upsertMany(state, action.payload);
      state.loading = false;
    });
    builder.addCase(removePortfolio.fulfilled, (state, action) => {
      portfolioAdapter.removeOne(state, action.payload);
      state.loading = false;
    });
    builder.addCase(updateBulkPortfolioOrder.fulfilled, (state, action) => {
      portfolioAdapter.updateMany(state,
        action.payload.map(item => ({
          id: item.id,
          changes: { order: item.order }
        }))
      );
      state.loading = false;
    });
}});
